import { Drawer, Descriptions, Skeleton, Typography, Divider, Tag } from "antd";
import { useIntl } from "react-intl";
import { shortenString } from "@lib/helpers/StringHelper";
import styled from "styled-components";
import dayjs from "dayjs";
import {
  CLIENT_MOOD_COLOR,
  MarketPlaceNoteValidation,
  NoteValidationResponseData,
  PROCESSED_STATUS_COLOR,
  STATUS_COLOR,
} from "@lib/types/NoteValidation";
import { DATE_FORMATS } from "@lib/constants/date";

const StyledDescriptions = styled(Descriptions)`
  th,
  td {
    padding: 5px !important;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-descriptions-item-label {
    width: 200px;
  }
`;

const SkeletonContent = () => (
  <StyledDescriptions bordered column={1}>
    {Array.from({ length: 9 }).map((_, index) => (
      <Descriptions.Item
        key={index}
        label={<Skeleton.Input active size="small" style={{ width: 100 }} />}
      >
        <Skeleton.Input active size="small" style={{ width: 200 }} />
      </Descriptions.Item>
    ))}
  </StyledDescriptions>
);

type Props = {
  open: boolean;
  onClose: () => void;
  item: MarketPlaceNoteValidation | null;
};

export const MarketPlaceNoteValidationDrawer = (props: Props) => {
  const { open, onClose, item } = props;
  const { formatMessage } = useIntl();

  const responseData = item?.responseData as NoteValidationResponseData | null;
  const hasResponseData = responseData && "id" in responseData;

  return (
    <StyledDrawer
      title={formatMessage(
        { id: "common.previewTitle" },
        { title: formatMessage({ id: "sidebar.marketPlace.noteValidation" }) },
      )}
      onClose={onClose}
      open={open}
      width={700}
      id="market-place-note-validation-drawer"
    >
      {!item ? (
        <SkeletonContent />
      ) : (
        <>
          {/* ── Meta ── */}
          <StyledDescriptions bordered column={1}>
            <Descriptions.Item label="ID">
              <Typography.Text copyable={{ text: item.id }}>
                {shortenString(item.id, 40)}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.language" })}>
              {item.language || "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.processedStatus" })}
            >
              {item.processedStatus ? (
                <Tag
                  color={
                    PROCESSED_STATUS_COLOR[item.processedStatus] ?? "default"
                  }
                >
                  {item.processedStatus}
                </Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>

            <Descriptions.Item label={formatMessage({ id: "common.status" })}>
              {item.status ? (
                <Tag color={STATUS_COLOR[item.status] ?? "default"}>
                  {item.status}
                </Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.createdAt" })}
            >
              {item.createdAt
                ? dayjs(item.createdAt).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME)
                : "-"}
            </Descriptions.Item>

            <Descriptions.Item
              label={formatMessage({ id: "common.updatedAt" })}
            >
              {item.updatedAt
                ? dayjs(item.updatedAt).format(DATE_FORMATS.DAY_MONTH_YEAR_TIME)
                : "-"}
            </Descriptions.Item>
          </StyledDescriptions>

          <Divider />

          {/* ── Raw note ── */}
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            {formatMessage({ id: "common.rawNote" })}
          </Typography.Title>
          <div dangerouslySetInnerHTML={{ __html: item.rawNote ?? "-" }} />

          <Divider />

          {/* ── Processed note ── */}
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            {formatMessage({ id: "common.processedNote" })}
          </Typography.Title>
          <Typography.Text>{item.processedNote ?? "-"}</Typography.Text>

          <Divider />

          {/* ── Suggestion ── */}
          <Typography.Title level={5} style={{ marginBottom: 8 }}>
            {formatMessage({ id: "common.suggestion" })}
          </Typography.Title>
          <Typography.Text>{item.suggestion ?? "-"}</Typography.Text>

          {/* ── Response data ── */}
          {hasResponseData && (
            <>
              <Divider />

              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                {formatMessage({ id: "common.responseData" })}
              </Typography.Title>

              <StyledDescriptions bordered column={1}>
                <Descriptions.Item
                  label={formatMessage({ id: "common.status" })}
                >
                  <Tag>{responseData.status}</Tag>
                </Descriptions.Item>

                <Descriptions.Item
                  label={formatMessage({ id: "common.clientMood" })}
                >
                  <Tag
                    color={
                      CLIENT_MOOD_COLOR[responseData.clientMood] ?? "default"
                    }
                  >
                    {responseData.clientMood}
                  </Tag>
                </Descriptions.Item>

                <Descriptions.Item
                  label={formatMessage({ id: "common.notes" })}
                >
                  <Typography.Text>{responseData.note}</Typography.Text>
                </Descriptions.Item>

                <Descriptions.Item
                  label={formatMessage({ id: "common.aiSuggestion" })}
                >
                  <Typography.Text>{responseData.aiSuggestion}</Typography.Text>
                </Descriptions.Item>

                <Descriptions.Item
                  label={formatMessage({ id: "common.keywords" })}
                >
                  {responseData.keywords.map((kw) => (
                    <Tag key={kw}>{kw}</Tag>
                  ))}
                </Descriptions.Item>
              </StyledDescriptions>
            </>
          )}
        </>
      )}
    </StyledDrawer>
  );
};
